import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps as MuiSelectProps,
} from "@mui/material";
import type { ReactNode } from "react";
import { OptionValue, SelectOption } from "../fieldTypes";

export type InputSelectValue = OptionValue | OptionValue[];

interface SelectProps extends Omit<
  MuiSelectProps<InputSelectValue>,
  "label" | "multiple" | "onChange" | "value"
> {
  label: ReactNode;
  onChange: (value: InputSelectValue) => void;
  options: SelectOption[];
  fullWidth?: boolean;
  multiple?: boolean;
  value: InputSelectValue;
  displayEmpty?: boolean;
  error?: boolean;
  errorMsg?: ReactNode;
}

const Select = ({
  label,
  onChange,
  options,
  fullWidth = true,
  multiple = false,
  value,
  displayEmpty = false,
  error = false,
  errorMsg = "",
  ...rest
}: SelectProps) => {
  const handleChange = (event: SelectChangeEvent<InputSelectValue>) => {
    const hasAll = options.some((option) => option.value === "all");
    const rawValue = event.target.value;
    const selectedValues = Array.isArray(rawValue) ? rawValue : [rawValue];
    let nextValue: InputSelectValue;

    if (multiple && hasAll) {
      if (selectedValues.length === 0) {
        nextValue = ["all"];
      } else if (selectedValues[0] === "all") {
        nextValue = selectedValues.filter((item) => item !== "all");
      } else if (selectedValues[selectedValues.length - 1] === "all") {
        nextValue = ["all"];
      } else {
        nextValue = selectedValues;
      }
      onChange(nextValue);
    } else {
      onChange(multiple ? selectedValues : rawValue);
    }
  };
  return (
    <FormControl fullWidth={fullWidth} sx={{ minWidth: 150 }} error={error}>
      <InputLabel size="small" id="demo-simple-select-label">
        {label}
      </InputLabel>
      <MuiSelect
        labelId="demo-simple-select-label"
        label={label}
        value={value}
        sx={{ minWidth: 150 }}
        onChange={handleChange}
        multiple={multiple}
        size="small"
        fullWidth={fullWidth}
        {...rest}
      >
        {displayEmpty && (
          <MenuItem key="_empty_" value="">
            - No selection -
          </MenuItem>
        )}
        {options.map(({ value: optionValue, label, caption }) => (
          <MenuItem key={optionValue} value={optionValue}>
            {label || caption || "- No selection -"}
          </MenuItem>
        ))}
      </MuiSelect>
      <FormHelperText>{errorMsg}</FormHelperText>
    </FormControl>
  );
};

export { Select };
